import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiContentSaveOutline } from "@mdi/js";
import { Box } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCreateChangeMaterialRequest } from "../../../../__generated__/orval/endpoints/change-material-requests/change-material-requests";
import { NewChangeMaterialRequestForm } from "./-internal-components/NewChangeMaterialRequestForm";
import {
  buildCreateChangeMaterialRequestPayload,
  type NewChangeMaterialRequestFormSchema,
  newChangeMaterialRequestFormDefaultValues,
  newChangeMaterialRequestFormSchema,
} from "./-schemas/newChangeMaterialRequestSchema";

const ChangeMaterialRequest: React.FC = () => {
  const { t } = useTranslation();

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: newChangeMaterialRequestFormDefaultValues,
    resolver: zodResolver(newChangeMaterialRequestFormSchema),
    shouldUnregister: true,
  });

  const { mutate, isPending } = useCreateChangeMaterialRequest({
    mutation: {
      onError: () => {
        enqueueSnackbar(t("mmRequestChild.errorMessage"), {
          variant: "error",
        });
      },
      onSuccess: (response) => {
        enqueueSnackbar(t("mmRequestChild.successMessage"), {
          variant: "success",
        });
        window.location.href = `/vbuyer/mm_requests?flash_message=${t("mmRequestChild.flashMessage")}${response.mm_request?.id}`;
      },
    },
  });

  const handleFilesChange = useCallback(
    (fileAttachments: string[]) => {
      setValue("file_attachments", fileAttachments);
    },
    [setValue],
  );

  const onSubmit: SubmitHandler<NewChangeMaterialRequestFormSchema> =
    useCallback(
      (formValues) => {
        const payload = buildCreateChangeMaterialRequestPayload(formValues);

        mutate({
          data: payload,
        });
      },
      [mutate],
    );

  return (
    <PageContainer
      backTo="/vbuyer/mm_requests/create_new_requests"
      maxWidth="md"
      showBackButton
      title={t("common.changeMaterialRequest")}
    >
      <AppCard>
        <NewChangeMaterialRequestForm
          control={control}
          onFilesChange={handleFilesChange}
          onSubmit={handleSubmit(onSubmit)}
          setValue={setValue}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              color="success"
              loading={isPending}
              startIcon={<Icon icon={mdiContentSaveOutline} />}
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </NewChangeMaterialRequestForm>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/change_material_requests/new",
)({
  component: ChangeMaterialRequest,
});
